// Pickup date rules shared by collection cards and the product page picker.
// Each picker reads its product's rules from data attributes, which Liquid fills
// from the product's pickup.* metafields:
//   data-pickup-days="sat"          only these weekdays (blank = any day)
//   data-pickup-cutoff="tue 17:00"  order by this Eastern time for that week's pickup
//   data-pickup-lead="2"            minimum days ahead when there is no cutoff (blank = 2)
// Holiday closures always apply. The saved cart property keeps the format the
// original product page script uses, "Saturday (10/10/2026)".

const STORAGE_KEY = "selectedPickupDate";
const STORE_TIME_ZONE = "America/New_York";
const DEFAULT_LEAD_DAYS = 2;
const UPCOMING_OPTION_COUNT = 4;
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SHORT_DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const SCOPE_SELECTOR = "[data-pickup-card], [data-pickup-pdp]";
const OPEN_CLASS = "is-open";

function stripTime(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateString(dateStr) {
  const parts = dateStr.split("-");
  return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
}

function toIsoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

// Wall-clock time at the bakery, so a cutoff means the same moment for every customer.
function storeNow() {
  const parts = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: STORE_TIME_ZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  })
    .formatToParts(new Date())
    .forEach((part) => {
      parts[part.type] = part.value;
    });
  return new Date(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute);
}

function getThanksgiving(year) {
  const nov1 = new Date(year, 10, 1);
  const firstThursday = nov1.getDay() <= 4 ? 5 - nov1.getDay() : 12 - nov1.getDay();
  return new Date(year, 10, firstThursday + 21);
}

function getEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

function getBlockedDates(year) {
  const blocked = [];
  const fixedHolidays = [new Date(year, 11, 24), new Date(year, 11, 25)];
  const easter = getEaster(year);
  const thanksgiving = getThanksgiving(year);

  [...fixedHolidays, easter].forEach((holiday) => {
    blocked.push(stripTime(addDays(holiday, -1)).getTime());
    blocked.push(stripTime(holiday).getTime());
    blocked.push(stripTime(addDays(holiday, 1)).getTime());
  });

  blocked.push(stripTime(addDays(thanksgiving, -1)).getTime());
  blocked.push(stripTime(thanksgiving).getTime());

  blocked.push(stripTime(new Date(year, 0, 1)).getTime());
  blocked.push(stripTime(new Date(year, 0, 2)).getTime());
  return blocked;
}

function isBlockedDate(date) {
  const checkDate = stripTime(date);
  const year = checkDate.getFullYear();
  const blockedDates = [...getBlockedDates(year), ...getBlockedDates(year + 1)];
  return blockedDates.includes(checkDate.getTime());
}

function parseDays(value) {
  return (value || "")
    .toLowerCase()
    .split(/[\s,]+/)
    .map((day) => SHORT_DAYS.indexOf(day.slice(0, 3)))
    .filter((day, index, all) => day >= 0 && all.indexOf(day) === index);
}

function parseCutoff(value) {
  const match = (value || "").trim().toLowerCase().match(/^([a-z]{3})[a-z]*\s+(\d{1,2}):(\d{2})$/);
  if (!match || !SHORT_DAYS.includes(match[1])) return null;
  return { day: SHORT_DAYS.indexOf(match[1]), hour: parseInt(match[2], 10), minute: parseInt(match[3], 10) };
}

function rulesFor(scope) {
  if (!scope.pickupRules) {
    const lead = parseInt(scope.dataset.pickupLead, 10);
    scope.pickupRules = {
      days: parseDays(scope.dataset.pickupDays),
      cutoff: parseCutoff(scope.dataset.pickupCutoff),
      lead: Number.isFinite(lead) && lead >= 0 ? lead : DEFAULT_LEAD_DAYS,
    };
  }
  return scope.pickupRules;
}

function earliestDate(lead) {
  return addDays(stripTime(new Date()), lead);
}

// The cutoff before a pickup date: e.g. "tue 17:00" for a Saturday is the Tuesday before it at 5pm.
function deadlineFor(date, cutoff) {
  const daysBack = (date.getDay() - cutoff.day + 7) % 7 || 7;
  const deadline = addDays(date, -daysBack);
  deadline.setHours(cutoff.hour, cutoff.minute, 0, 0);
  return deadline;
}

function dayList(days) {
  const names = days.map((day) => WEEKDAYS[day].toLowerCase());
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}` : names[0];
}

function getDateError(dateStr, rules) {
  if (!dateStr) return "pick a pickup date first";
  const selected = stripTime(parseDateString(dateStr));
  if (rules.days.length && !rules.days.includes(selected.getDay())) {
    return `pickup is ${dayList(rules.days)} only`;
  }
  if (rules.cutoff) {
    if (storeNow() >= deadlineFor(selected, rules.cutoff)) {
      return `orders for that ${WEEKDAYS[selected.getDay()].toLowerCase()} are closed. pick the next one.`;
    }
  } else if (selected < earliestDate(rules.lead)) {
    return `pick a date at least ${rules.lead} days out`;
  }
  if (isBlockedDate(selected)) return "closed for the holiday. pick another day.";
  return null;
}

function upcomingDates(rules) {
  const dates = [];
  const start = stripTime(new Date());
  for (let offset = 0; offset < 120 && dates.length < UPCOMING_OPTION_COUNT; offset++) {
    const iso = toIsoDate(addDays(start, offset));
    if (!getDateError(iso, rules)) dates.push(iso);
  }
  return dates;
}

function propertyValue(dateStr) {
  const selected = parseDateString(dateStr);
  const formatted = `${selected.getMonth() + 1}/${selected.getDate()}/${selected.getFullYear()}`;
  return `${WEEKDAYS[selected.getDay()]} (${formatted})`;
}

function shortLabel(dateStr) {
  const selected = parseDateString(dateStr);
  return `${SHORT_DAYS[selected.getDay()]}, ${MONTHS[selected.getMonth()]} ${selected.getDate()}`;
}

function scopes() {
  return document.querySelectorAll(SCOPE_SELECTOR);
}

function syncPrompt(scope) {
  const control = scope.querySelector("[data-pickup-date]");
  const wrap = scope.querySelector("[data-pickup-date-wrap]");
  const prompt = wrap && wrap.querySelector(".pickup-card__prompt");
  if (!control || !prompt) return;
  prompt.textContent = control.value ? shortLabel(control.value) : wrap.dataset.pickupPlaceholder || "choose date";
  wrap.classList.toggle("is-set", control.value !== "");
}

function setMessage(scope, text, isError) {
  const message = scope.querySelector("[data-pickup-message]");
  if (!message) return;
  message.textContent = text;
  message.classList.toggle("is-error", Boolean(isError));
}

function ensureOption(select, dateStr) {
  if ([...select.options].some((option) => option.value === dateStr)) return;
  const option = new Option(shortLabel(dateStr), dateStr);
  select.add(option);
}

// Returns true when the date was valid for this picker and applied.
// announce=false is used when copying another picker's date: invalid dates are skipped quietly.
function applyDate(scope, dateStr, announce) {
  const control = scope.querySelector("[data-pickup-date]");
  const hidden = scope.querySelector("[data-pickup-property]");
  if (!control || !hidden) return false;
  const error = getDateError(dateStr, rulesFor(scope));
  if (error) {
    if (announce) {
      control.value = "";
      hidden.value = "";
      syncPrompt(scope);
      setMessage(scope, error, true);
    }
    return false;
  }
  if (control.tagName === "SELECT") ensureOption(control, dateStr);
  control.value = dateStr;
  hidden.value = propertyValue(dateStr);
  setMessage(scope, scope.hasAttribute("data-pickup-pdp") ? `ready for pickup ${propertyValue(dateStr).toLowerCase()}` : "", false);
  syncPrompt(scope);
  return true;
}

function initScope(scope) {
  const control = scope.querySelector("[data-pickup-date]");
  if (!control || control.dataset.pickupReady === "true") return;
  control.dataset.pickupReady = "true";
  const rules = rulesFor(scope);
  const wrap = scope.querySelector("[data-pickup-date-wrap]");

  if (control.tagName === "SELECT") {
    const placeholder = rules.days.length === 1 ? `choose ${WEEKDAYS[rules.days[0]].toLowerCase()}` : "choose date";
    if (wrap) wrap.dataset.pickupPlaceholder = placeholder;
    control.options[0].textContent = placeholder;
    upcomingDates(rules).forEach((dateStr) => ensureOption(control, dateStr));
  } else {
    control.min = toIsoDate(earliestDate(rules.lead));
    control.addEventListener("click", (event) => {
      event.stopPropagation();
      if (typeof control.showPicker === "function" && window.matchMedia("(pointer: fine)").matches) {
        try {
          control.showPicker();
        } catch (error) {}
      }
    });
  }
  syncPrompt(scope);

  control.addEventListener("change", () => {
    if (!applyDate(scope, control.value, true)) return;
    sessionStorage.setItem(STORAGE_KEY, control.value);
    scopes().forEach((other) => {
      if (other !== scope) applyDate(other, control.value, false);
    });
  });
}

function setOpen(wrapper, open) {
  if (!wrapper) return;
  wrapper.classList.toggle(OPEN_CLASS, open);
  const toggle = wrapper.querySelector("[data-pickup-toggle]");
  if (toggle) toggle.setAttribute("aria-expanded", String(open));
}

function closeAll(except) {
  document.querySelectorAll(`.product-card-wrapper--pickup.${OPEN_CLASS}`).forEach((wrapper) => {
    if (wrapper !== except) setOpen(wrapper, false);
  });
}

function scopeForForm(form) {
  const card = form.closest("[data-pickup-card]");
  if (card) return card;
  // form.id would return the variant input named "id", so read the attribute.
  const formId = form.getAttribute("id");
  if (!formId) return null;
  return [...document.querySelectorAll("[data-pickup-pdp]")].find((scope) => scope.dataset.pickupForm === formId) || null;
}

document.addEventListener("click", (event) => {
  const toggle = event.target.closest("[data-pickup-toggle]");
  if (toggle) {
    event.preventDefault();
    event.stopPropagation();
    const wrapper = toggle.closest(".product-card-wrapper--pickup");
    closeAll(wrapper);
    setOpen(wrapper, true);
    const control = wrapper.querySelector("[data-pickup-date]");
    // Opening the native picker in the same tap saves the customer a step.
    if (control && !control.value && typeof control.showPicker === "function") {
      try {
        control.showPicker();
      } catch (error) {}
    }
    return;
  }

  const close = event.target.closest("[data-pickup-close]");
  if (close) {
    event.preventDefault();
    event.stopPropagation();
    setOpen(close.closest(".product-card-wrapper--pickup"), false);
  }
});

document.addEventListener("pointerdown", (event) => {
  if (event.target.closest("[data-pickup-card], [data-pickup-toggle]")) return;
  closeAll(null);
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") closeAll(null);
});

document.addEventListener(
  "submit",
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const scope = scopeForForm(form);
    if (!scope) return;
    const hidden = scope.querySelector("[data-pickup-property]");
    // Recharge copies first-order properties onto every renewal, so a subscription gets no fixed date;
    // the bake list dates each renewal instead.
    const sellingPlan = form.querySelector('[name="selling_plan"]');
    const isSubscription = Boolean(sellingPlan && sellingPlan.value);
    hidden.disabled = isSubscription;
    if (isSubscription) return;
    const control = scope.querySelector("[data-pickup-date]");
    const value = control ? control.value : "";
    if (getDateError(value, rulesFor(scope))) {
      event.preventDefault();
      event.stopPropagation();
      applyDate(scope, value, true);
      if (control) control.focus();
      return;
    }
    hidden.value = propertyValue(value);
    sessionStorage.setItem(STORAGE_KEY, value);
    const wrapper = scope.closest(".product-card-wrapper--pickup");
    if (wrapper) setTimeout(() => setOpen(wrapper, false), 400);
  },
  true
);

// "slice it for me" on cards (snippets/slice-option.liquid) adds the Sliced variant instead of Whole.
function syncSlice(box) {
  const variantInput = box.form && box.form.querySelector('[name="id"]');
  if (variantInput) variantInput.value = box.checked ? box.dataset.slicedVariant : box.dataset.wholeVariant;
}

document.addEventListener("change", (event) => {
  if (event.target.matches("[data-slice-toggle]")) syncSlice(event.target);
});

function boot() {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  scopes().forEach((scope) => {
    initScope(scope);
    if (stored) applyDate(scope, stored, false);
  });
  // The browser may restore a ticked box on back/forward without firing change.
  document.querySelectorAll("[data-slice-toggle]").forEach(syncSlice);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
